import { IColorUser, useAuthContext, useSessionStorageState } from '@color/lib';
import constate from 'constate';
import { useCallback, useEffect } from 'react';

import { useParamUser } from './util';

export interface ParticipantContext {
  resetState: () => void;
  participant?: IColorUser;
  setParticipant: (user: IColorUser) => void;
  participantIsADependent: boolean;
  participantIsSelected: boolean;
  accountHolder?: IColorUser;
  dependents?: IColorUser[];
}

function useParticipant(): ParticipantContext {
  const { currentUser } = useAuthContext();

  const [participantId, setParticipantId] = useSessionStorageState<number | undefined>(
    'selectedParticipant',
    undefined
  );

  const participantIsADependent = Boolean(
    participantId && currentUser && participantId !== currentUser.id
  );
  const participantIsSelected = Boolean(participantId);
  const paramParticipant = useParamUser();

  const getParticipant = () => {
    if (!participantId) {
      return currentUser;
    }
    if (currentUser?.id === participantId) {
      return currentUser;
    }
    return currentUser?.dependents?.find((u) => u.id === participantId) || currentUser;
    // intentionally not throwing error if not found, in case user just not loaded yet
  };

  /**
   * `useEffect` is often dangerous and unnecessary. This usage needs review by #ux-programs team.
   * See https://getcolor.atlassian.net/browse/UXTASK-1406
   */
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    if (paramParticipant && !participantIsSelected) {
      setParticipantId(paramParticipant.id);
    }
  }, [setParticipantId, paramParticipant, participantIsSelected]);

  const resetState = useCallback(() => {
    if (!paramParticipant) {
      setParticipantId(undefined);
    }
  }, [paramParticipant, setParticipantId]);

  const setParticipant = useCallback(
    (user: IColorUser) => {
      setParticipantId(user.id);
    },
    [setParticipantId]
  );
  return {
    resetState,
    participant: getParticipant(),
    setParticipant,
    participantIsADependent,
    participantIsSelected,
    accountHolder: currentUser,
    dependents: currentUser?.dependents,
  };
}

export const [ParticipantProvider, useParticipantContext] = constate(useParticipant);
